// Copyright ©️ 2026 eVolve MEP, LLC

import './trigger-on-visible.css';

import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Collapse, Loader, type LoaderProps, Flex } from '@mantine/core';

// oxlint-disable-next-line typescript/no-explicit-any
export const useOnScreen = (ref: RefObject<any>) => {
  const [isIntersecting, setIntersecting] = useState(false);

  const observer = useMemo(
    () => new IntersectionObserver(([entry]) => setIntersecting(entry?.isIntersecting ?? false)),
    [],
  );

  useEffect(() => {
    observer.observe(ref.current);
    return () => {
      observer.disconnect();
    };
  }, [observer, ref]);

  return isIntersecting;
};

export const TriggerOnVisible = ({
  alwaysRenderLoader,
  loading = false,
  loaderProps,
  onVisible,
  height,
  useCollapse = false,
}: {
  alwaysRenderLoader?: boolean;
  loading?: boolean;
  loaderProps?: LoaderProps;
  onVisible: () => void;
  height?: number;
  useCollapse?: boolean;
}) => {
  const ref = useRef(null);
  const onScreen = useOnScreen(ref);
  useEffect(() => {
    if (onScreen && !loading) onVisible();
  }, [loading, onScreen, onVisible]);

  return (
    <Box
      ref={ref}
      display="flex"
      style={loading ? { alignItems: 'center' } : undefined}
      className={loading ? undefined : 'remove-stack-height'}
    >
      <div
        style={{
          height: 1,
          width: 1,
          marginTop: -1,
          marginLeft: -1,
        }}
      />
      {useCollapse ? (
        <Collapse expanded={alwaysRenderLoader || loading}>
          <Box h={height} mah={height}>
            <Loader type="dots" m="xs" {...loaderProps} />
          </Box>
        </Collapse>
      ) : (
        loading && (
          <Flex h={height} mah={height} align="center">
            <Loader type="dots" m="xs" {...loaderProps} />
          </Flex>
        )
      )}
    </Box>
  );
};
